/**
 * Bible RAG — BM25 index + semantic retrieval over loaded Bible JSON.
 *
 * Bible JSON shape:
 *   [ { name: "Genesis", chapters: [ ["verse1", "verse2", ...], ... ] }, ... ]
 */

const STOPWORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with','by',
  'is','was','are','were','be','been','being','have','has','had','do','does',
  'did','will','would','could','should','may','might','shall','that','this',
  'it','he','she','they','we','you','i','me','him','her','them','us',
  'his','its','their','our','your','my','which','who','whom','what','when',
  'where','how','if','as','so','not','no','nor','yet','both','either',
  'from','into','through','during','before','after','above','below',
  'then','than','too','very','just','more','also','up','out','about',
]);

function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOPWORDS.has(t));
}

/**
 * Build a BM25 index over the entire loaded Bible.
 * Call once when bibleData changes (expensive — ~100ms for full Bible).
 */
export function buildIndex(bibleData) {
  const verses = []; // flat list of all verse records
  const invertedIndex = new Map(); // token → Set of verse indices
  const df = new Map(); // token → document frequency

  for (let bIdx = 0; bIdx < bibleData.length; bIdx++) {
    const book = bibleData[bIdx];
    for (let cIdx = 0; cIdx < book.chapters.length; cIdx++) {
      const chapter = book.chapters[cIdx];
      for (let vIdx = 0; vIdx < chapter.length; vIdx++) {
        const text = chapter[vIdx];
        const tokens = tokenize(text);
        const vRecord = {
          idx: verses.length,
          bookIdx: bIdx,
          chapterIdx: cIdx,
          verseIdx: vIdx,
          bookName: book.name,
          text,
          tokens,
        };
        verses.push(vRecord);

        // Build inverted index (unique tokens per verse for df)
        const seen = new Set();
        for (const token of tokens) {
          if (!invertedIndex.has(token)) invertedIndex.set(token, []);
          invertedIndex.get(token).push(vRecord.idx);
          if (!seen.has(token)) {
            df.set(token, (df.get(token) || 0) + 1);
            seen.add(token);
          }
        }
      }
    }
  }

  const avgVerseLength = verses.reduce((s, v) => s + v.tokens.length, 0) / (verses.length || 1);

  return { verses, invertedIndex, df, N: verses.length, avgVerseLength };
}

/**
 * BM25 search over the index.
 * Returns top-K verse objects: { book, chapter, verse, text }
 */
export function search(query, index, topK = 6) {
  if (!index || !query.trim()) return [];

  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];

  const k1 = 1.5;
  const b = 0.75;
  const scores = new Map();

  for (const token of queryTokens) {
    const postings = index.invertedIndex.get(token);
    if (!postings) continue;

    const docFreq = index.df.get(token) || 0;
    const idf = Math.log((index.N - docFreq + 0.5) / (docFreq + 0.5) + 1);

    for (const vIdx of postings) {
      const verse = index.verses[vIdx];
      const tf = verse.tokens.filter(t => t === token).length;
      const dl = verse.tokens.length;
      const tfNorm = (tf * (k1 + 1)) / (tf + k1 * (1 - b + b * (dl / index.avgVerseLength)));
      scores.set(vIdx, (scores.get(vIdx) || 0) + idf * tfNorm);
    }
  }

  return Array.from(scores.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, topK)
    .map(([vIdx]) => {
      const v = index.verses[vIdx];
      return {
        book: v.bookName,
        chapter: v.chapterIdx + 1,
        verse: v.verseIdx + 1,
        text: v.text,
      };
    });
}

/**
 * Get all verses in a chapter as context (preserves exact translation wording).
 */
export function getChapterVerses(bibleData, bookIdx, chapterIdx) {
  const book = bibleData[bookIdx];
  if (!book) return [];
  const chapter = book.chapters[chapterIdx];
  if (!chapter) return [];
  return chapter.map((text, i) => ({
    book: book.name,
    chapter: chapterIdx + 1,
    verse: i + 1,
    text,
  }));
}

/**
 * Format verse list into a PASSAGES block for the LLM prompt.
 */
export function buildContext(verses) {
  return verses
    .map(v => `[${v.book} ${v.chapter}:${v.verse}] ${v.text}`)
    .join('\n');
}

/**
 * After generation, scan the response text for Bible references and verify
 * them against the loaded Bible data. Returns an array of warning strings.
 */
export function verifyCitations(responseText, bibleData) {
  // Matches: "Genesis 1:1", "1 Corinthians 13:4", "Song of Solomon 2:3"
  const refPattern = /\b((?:[123]\s+)?[A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)*)\s+(\d+):(\d+)\b/g;
  const warnings = [];
  let match;

  while ((match = refPattern.exec(responseText)) !== null) {
    const [, rawBook, rawChapter, rawVerse] = match;
    const bookName = rawBook.trim();
    const chapter = parseInt(rawChapter, 10);
    const verse = parseInt(rawVerse, 10);

    const book = bibleData.find(
      b => b.name.toLowerCase() === bookName.toLowerCase()
    );
    if (!book) {
      warnings.push(`"${bookName}" not found in the loaded Bible text.`);
      continue;
    }
    const chapterArr = book.chapters[chapter - 1];
    if (!chapterArr || !chapterArr[verse - 1]) {
      warnings.push(`${bookName} ${chapter}:${verse} does not exist in the loaded text.`);
    }
  }

  return warnings;
}

/**
 * Build the full augmented prompt for the LLM.
 * Combines the always-present current chapter with BM25 search results.
 * Uses a simpler format for small models (1B) and a richer format for larger ones.
 */
export function buildPrompt({ query, ragResults, currentChapterVerses, simple = false }) {
  // Deduplicate: don't repeat current-chapter verses already in rag results
  const ragKeys = new Set(ragResults.map(v => `${v.book}${v.chapter}:${v.verse}`));
  const extraFromChapter = currentChapterVerses.filter(
    v => !ragKeys.has(`${v.book}${v.chapter}:${v.verse}`)
  );

  const allVerses = [...ragResults, ...extraFromChapter];
  const passages = buildContext(allVerses);

  if (simple) {
    // Short, direct format — works better with small (1B) models
    return `Bible passages:\n${passages}\n\nQuestion: ${query}\n\nAnswer using only the passages above. Quote the relevant verse, then answer briefly.`;
  }

  // Full structured format for capable models (3B+)
  return `PASSAGES FROM SCRIPTURE:\n${passages}\n\nQUESTION: ${query}\n\nINSTRUCTIONS:\n1. Quote the most relevant passage above using [Book Chapter:Verse] format.\n2. Answer based only on the passages provided.\n3. If no passage addresses the question, say: "The provided passages do not address this question."\nDo not use knowledge from outside the PASSAGES block.`;
}
